import type {
  HarnessAssistantEvent,
  HarnessResultEvent,
  HarnessResultSuccessEvent,
  HarnessResultErrorEvent,
  HarnessSystemInitEvent,
  HarnessCompactBoundaryEvent,
  HarnessTaskStartedEvent,
  HarnessTaskProgressEvent,
  HarnessRateLimitEvent,
} from "../harness/index.js";
import type { TaskContext, ActivityEventSummary } from "@project/shared";
import type { QueryHost } from "./query-executor.js";
import { createServiceLogger } from "../utils/logger.js";

const logger = createServiceLogger("event-handlers");

function safeVoid(promise: void | Promise<unknown>, context: string): void {
  if (promise && typeof (promise as Promise<unknown>).catch === "function") {
    (promise as Promise<unknown>).catch((err) => {
      process.stderr.write(`[safeVoid] ${context}: ${err}\n`);
    });
  }
}

export type UsageInfo = {
  input_tokens?: number;
  cache_read_input_tokens?: number;
  cache_creation_input_tokens?: number;
};

/** Convert a resetsAt value (epoch seconds, epoch ms, or ISO string) to an ISO string. */
function epochSecondsToISO(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (typeof value !== "number" || value <= 0) return undefined;
  // Heuristic: epoch seconds for dates after 2000 are < 1e12; milliseconds are >= 1e12
  const ms = value < 1e12 ? value * 1000 : value;
  return new Date(ms).toISOString();
}

export async function processAssistantEvent(
  event: HarnessAssistantEvent,
  host: QueryHost,
  turnToolCalls: ActivityEventSummary[],
): Promise<void> {
  const { content } = event.message;
  const turnTextParts: string[] = [];

  for (const block of content) {
    if (block.type === "text" && block.text) {
      turnTextParts.push(block.text);
      host.connection.sendEvent({ type: "message", content: block.text });
      await host.callbacks.onEvent({ type: "message", content: block.text });
    } else if (block.type === "tool_use" && block.name) {
      const inputStr = typeof block.input === "string" ? block.input : JSON.stringify(block.input);
      const isContentTool = ["edit", "write"].includes(block.name.toLowerCase());
      const inputLimit = isContentTool ? 10_000 : 500;
      const summary: ActivityEventSummary = {
        tool: block.name,
        input: inputStr.slice(0, inputLimit),
        timestamp: new Date().toISOString(),
      };
      turnToolCalls.push(summary);
      host.connection.sendEvent({ type: "tool_use", tool: block.name, input: inputStr });
      await host.callbacks.onEvent({ type: "tool_use", tool: block.name, input: inputStr });
    }
  }
}

export const API_ERROR_PATTERN = /API Error: (?:[45]\d\d|terminated)/;
const IMAGE_ERROR_PATTERN = /Could not process image/i;
const AUTH_ERROR_PATTERN =
  /Not logged in|Please run \/login|authentication failed|invalid.*token|unauthorized/i;

export function isAuthError(msg: string): boolean {
  return AUTH_ERROR_PATTERN.test(msg);
}

function isRetriableMessage(msg: string): boolean {
  if (IMAGE_ERROR_PATTERN.test(msg)) return true;
  if (API_ERROR_PATTERN.test(msg)) return true;
  return false;
}

function aggregateModelUsage(modelUsage: Record<string, unknown>): {
  queryInputTokens: number;
  contextWindow: number;
  totalInputTokens: number;
  totalCacheRead: number;
  totalCacheCreation: number;
} {
  let queryInputTokens = 0;
  let contextWindow = 0;
  let totalInputTokens = 0;
  let totalCacheRead = 0;
  let totalCacheCreation = 0;
  for (const data of Object.values(modelUsage)) {
    const d = data as {
      inputTokens?: number;
      cacheReadInputTokens?: number;
      cacheCreationInputTokens?: number;
    };
    const input = d.inputTokens ?? 0;
    const cacheRead = d.cacheReadInputTokens ?? 0;
    const cacheCreation = d.cacheCreationInputTokens ?? 0;
    totalInputTokens += input;
    totalCacheRead += cacheRead;
    totalCacheCreation += cacheCreation;
    queryInputTokens += input + cacheRead + cacheCreation;
    const cw = (data as { contextWindow?: number }).contextWindow ?? 0;
    if (cw > contextWindow) contextWindow = cw;
  }
  return { queryInputTokens, contextWindow, totalInputTokens, totalCacheRead, totalCacheCreation };
}

function emitContextUpdate(
  modelUsage: Record<string, unknown>,
  host: QueryHost,
  context: TaskContext,
  lastAssistantUsage?: UsageInfo,
): void {
  const usage = aggregateModelUsage(modelUsage);
  let { contextWindow } = usage;

  // Override contextWindow when 1M beta is enabled but SDK reports ≤200K
  const settings = context.agentSettings ?? host.config.agentSettings ?? {};
  const has1mBeta = (settings.betas as string[] | undefined)?.includes("context-1m-2025-08-07");
  if (has1mBeta && contextWindow > 0 && contextWindow <= 200_000) {
    contextWindow = 1_000_000;
  }

  if (contextWindow > 0) {
    // Current context fill = last API call's input tokens (per-call, not cumulative)
    const currentContextTokens = lastAssistantUsage
      ? (lastAssistantUsage.input_tokens ?? 0) +
        (lastAssistantUsage.cache_read_input_tokens ?? 0) +
        (lastAssistantUsage.cache_creation_input_tokens ?? 0)
      : usage.queryInputTokens;

    host.connection.sendEvent({
      type: "context_update",
      contextTokens: currentContextTokens,
      contextWindow,
      inputTokens: usage.totalInputTokens,
      cacheReadInputTokens: usage.totalCacheRead,
      cacheCreationInputTokens: usage.totalCacheCreation,
      totalTokensUsed: usage.queryInputTokens,
    });
  }
}

function trackCostSpending(host: QueryHost, context: TaskContext, cumulativeTotal: number): void {
  if (cumulativeTotal > 0 && context.agentId && context._runnerSessionId) {
    const breakdown = host.costTracker.modelBreakdown;
    host.connection.trackSpending({
      agentId: context.agentId,
      sessionId: context._runnerSessionId,
      totalCostUsd: cumulativeTotal,
      onSubscription: host.config.mode === "pm" || !!process.env.CLAUDE_CODE_OAUTH_TOKEN,
      modelUsage: breakdown.length > 0 ? breakdown : undefined,
    });
  }
}

function handleSuccessResult(
  event: HarnessResultSuccessEvent,
  host: QueryHost,
  context: TaskContext,
  startTime: number,
  lastAssistantUsage?: UsageInfo,
): { totalCostUsd: number; retriable: boolean } {
  const durationMs = Date.now() - startTime;
  const summary = event.result || "Task completed.";
  const retriable = isRetriableMessage(summary);

  const cumulativeTotal = host.costTracker.addQueryCost(event.total_cost_usd);

  const { modelUsage } = event;
  if (modelUsage && typeof modelUsage === "object") {
    host.costTracker.addModelUsage(
      modelUsage as Parameters<typeof host.costTracker.addModelUsage>[0],
    );
  }

  host.connection.sendEvent({ type: "completed", summary, costUsd: cumulativeTotal, durationMs });

  if (modelUsage && typeof modelUsage === "object") {
    emitContextUpdate(modelUsage as Record<string, unknown>, host, context, lastAssistantUsage);
  }

  trackCostSpending(host, context, cumulativeTotal);

  return { totalCostUsd: cumulativeTotal, retriable };
}

function handleErrorResult(
  event: HarnessResultErrorEvent,
  host: QueryHost,
): { retriable: boolean; staleSession?: boolean; authError?: boolean } {
  const errorMsg =
    event.errors.length > 0 ? event.errors.join(", ") : `Agent stopped: ${event.subtype}`;

  // Check for stale session error pattern
  const isStaleSession = errorMsg.includes("No conversation found with session ID");

  if (isStaleSession) {
    // Suppress the error event for stale session - this will be handled by session recovery
    return { retriable: false, staleSession: true };
  }

  // Check for auth error pattern — will be handled by auth recovery
  if (isAuthError(errorMsg)) {
    host.connection.sendEvent({ type: "error", message: errorMsg });
    return { retriable: false, authError: true };
  }

  const retriable = isRetriableMessage(errorMsg);
  host.connection.sendEvent({ type: "error", message: errorMsg });
  return { retriable };
}

function handleResultEvent(
  event: HarnessResultEvent,
  host: QueryHost,
  context: TaskContext,
  startTime: number,
  lastAssistantUsage?: UsageInfo,
): {
  totalCostUsd: number;
  retriable: boolean;
  resultSummary?: string;
  staleSession?: boolean;
  authError?: boolean;
} {
  const resultSummary =
    event.subtype === "success"
      ? (event as HarnessResultSuccessEvent).result
      : (event as HarnessResultErrorEvent).errors.join(", ");

  if (event.subtype === "success") {
    const result = handleSuccessResult(
      event as HarnessResultSuccessEvent,
      host,
      context,
      startTime,
      lastAssistantUsage,
    );
    return { ...result, resultSummary };
  }

  const result = handleErrorResult(event as HarnessResultErrorEvent, host);
  return { totalCostUsd: 0, ...result, resultSummary };
}

export async function emitResultEvent(
  event: HarnessResultEvent,
  host: QueryHost,
  context: TaskContext,
  startTime: number,
  lastAssistantUsage?: UsageInfo,
): Promise<{
  retriable: boolean;
  resultSummary?: string;
  staleSession?: boolean;
  authError?: boolean;
}> {
  const result = handleResultEvent(event, host, context, startTime, lastAssistantUsage);
  const durationMs = Date.now() - startTime;

  if (event.subtype === "success") {
    const successEvent = event as HarnessResultSuccessEvent;
    const summary = successEvent.result || "Task completed.";
    await host.callbacks.onEvent({
      type: "completed",
      summary,
      costUsd: result.totalCostUsd,
      durationMs,
    });
  } else if (!result.staleSession) {
    // Only emit error event if it's not a stale session (which is handled by recovery logic)
    const errorEvent = event as HarnessResultErrorEvent;
    const errorMsg =
      errorEvent.errors.length > 0
        ? errorEvent.errors.join(", ")
        : `Agent stopped: ${errorEvent.subtype}`;
    await host.callbacks.onEvent({ type: "error", message: errorMsg });
  }

  return {
    retriable: result.retriable,
    resultSummary: result.resultSummary,
    staleSession: result.staleSession,
    authError: result.authError,
  };
}

export function handleRateLimitEvent(
  event: HarnessRateLimitEvent,
  host: QueryHost,
): string | undefined {
  const { rate_limit_info } = event;
  logger.info("Rate limit event received", { rate_limit_info });
  const status = rate_limit_info.status;

  // Send structured rate limit update for persistence
  // On rejection without explicit utilization, default to 1.0 (fully consumed)
  const utilization = rate_limit_info.utilization ?? (status === "rejected" ? 1.0 : undefined);
  if (utilization !== undefined && rate_limit_info.rateLimitType) {
    host.connection.sendEvent({
      type: "rate_limit_update",
      rateLimitType: rate_limit_info.rateLimitType,
      utilization,
      status,
    });
  }

  if (status === "rejected") {
    const resetsAt = epochSecondsToISO(rate_limit_info.resetsAt);
    const resetsAtDisplay = resetsAt ?? "unknown";
    const message = `Rate limit rejected (type: ${rate_limit_info.rateLimitType ?? "unknown"}, resets at: ${resetsAtDisplay})`;
    host.connection.sendEvent({ type: "error", message });
    safeVoid(host.callbacks.onEvent({ type: "error", message }), "rateLimitRejected");
    return resetsAt;
  } else if (status === "allowed_warning") {
    const utilizationLabel = rate_limit_info.utilization
      ? `${Math.round(rate_limit_info.utilization * 100)}%`
      : "high";
    const message = `Rate limit warning: ${utilizationLabel} utilization (type: ${rate_limit_info.rateLimitType ?? "unknown"})`;
    host.connection.sendEvent({ type: "thinking", message });
    safeVoid(host.callbacks.onEvent({ type: "thinking", message }), "rateLimitWarning");
  }
  return undefined;
}

export async function handleSystemEvent(
  event: HarnessSystemInitEvent,
  host: QueryHost,
  context: TaskContext,
  sessionIdStored: boolean,
): Promise<boolean> {
  if (event.subtype !== "init") return false;
  if (event.session_id && !sessionIdStored) {
    host.connection.storeSessionId(event.session_id);
    context.claudeSessionId = event.session_id;
  }
  await host.callbacks.onEvent({
    type: "thinking",
    message: `Agent initialized (model: ${event.model})`,
  });
  return !!(event.session_id && !sessionIdStored);
}

export function handleSystemSubevents(
  systemEvent: HarnessCompactBoundaryEvent | HarnessTaskStartedEvent | HarnessTaskProgressEvent,
  host: QueryHost,
): void {
  if (systemEvent.subtype === "compact_boundary") {
    safeVoid(
      host.callbacks.onEvent({
        type: "context_compacted",
        trigger: systemEvent.compact_metadata.trigger,
        preTokens: systemEvent.compact_metadata.pre_tokens,
      }),
      "compactBoundary",
    );
  } else if (systemEvent.subtype === "task_started") {
    safeVoid(
      host.callbacks.onEvent({
        type: "subagent_started",
        sdkTaskId: systemEvent.task_id,
        description: systemEvent.description,
      }),
      "taskStarted",
    );
  } else if (systemEvent.subtype === "task_progress") {
    safeVoid(
      host.callbacks.onEvent({
        type: "subagent_progress",
        sdkTaskId: systemEvent.task_id,
        description: systemEvent.description,
        toolUses: systemEvent.usage?.tool_uses ?? 0,
        durationMs: systemEvent.usage?.duration_ms ?? 0,
      }),
      "taskProgress",
    );
  }
}

export function handleToolProgressEvent(event: unknown, host: QueryHost): void {
  const msg = event as { tool_name?: string; elapsed_time_seconds?: number };
  safeVoid(
    host.callbacks.onEvent({
      type: "tool_progress",
      toolName: msg.tool_name ?? "",
      elapsedSeconds: msg.elapsed_time_seconds ?? 0,
    }),
    "toolProgress",
  );
}

export async function handleAssistantCase(
  event: HarnessAssistantEvent,
  host: QueryHost,
  turnToolCalls: ActivityEventSummary[],
): Promise<UsageInfo | undefined> {
  await processAssistantEvent(event, host, turnToolCalls);
  const msgUsage = (event.message as { usage?: UsageInfo }).usage;
  return msgUsage ?? undefined;
}

export async function handleResultCase(
  event: HarnessResultEvent,
  host: QueryHost,
  context: TaskContext,
  startTime: number,
  isTyping: boolean,
  lastAssistantUsage: UsageInfo | undefined,
): Promise<{
  retriable: boolean;
  resultSummary?: string;
  staleSession?: boolean;
  authError?: boolean;
  stoppedTyping: boolean;
}> {
  let stoppedTyping = false;
  if (isTyping) {
    host.connection.sendTypingStop();
    stoppedTyping = true;
  }
  const resultInfo = await emitResultEvent(event, host, context, startTime, lastAssistantUsage);
  return {
    retriable: resultInfo.retriable,
    resultSummary: resultInfo.resultSummary,
    staleSession: resultInfo.staleSession,
    authError: resultInfo.authError,
    stoppedTyping,
  };
}
